import type { ProductRecommendations as RecommendationsSchema } from '../../../amplienceTypes/schemas/imported/product-recommendations-schema';
import type { ReactNode } from 'react';

import { createContext, useState, useEffect, useMemo, useContext } from 'react';

export enum CustomEvents {
  EXPERIENCE_OPTIONS_REQUEST = 'experience-options-request',
  EXPERIENCE_OPTIONS_RESPONSE = 'experience-options-response',
}

export interface ExperienceOptions {
  experienceId?: string;
  experienceName?: string;
  translations?: Record<string, string>;
  variation?: number | string;
  variables?: Record<string, string | number | boolean>;
  callbacks?: Record<string, (args: unknown) => void>;
  recommendationsContent?: RecommendationsSchema;
}

export type ExperienceContextInterface = {
  experienceOptions: {
    tk51: ExperienceOptions;
  };
};

const defaultExperienceOptions: ExperienceContextInterface['experienceOptions'] = {
  tk51: {},
};

const ExperienceContext = createContext<ExperienceContextInterface>({
  experienceOptions: defaultExperienceOptions,
});

export const useExperienceContext = () => useContext(ExperienceContext);

export const ExperienceOptionsProvider = ({ children }: { children: ReactNode }) => {
  const [experienceOptions, setExperienceOptions] =
    useState<ExperienceContextInterface['experienceOptions']>(defaultExperienceOptions);
  const requestEventsArray = useMemo(() => [CustomEvents.EXPERIENCE_OPTIONS_REQUEST], []);
  const responseEventsArray = useMemo(() => [CustomEvents.EXPERIENCE_OPTIONS_RESPONSE], []);

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent;

      setExperienceOptions({
        ...experienceOptions,
        [customEvent.detail.experienceName]: { ...customEvent.detail },
      });
    };

    responseEventsArray.forEach(event => document.addEventListener(event as string, handler));

    return () => {
      responseEventsArray.forEach(event => document.removeEventListener(event as string, handler));
    };
  }, [experienceOptions, responseEventsArray]);

  useEffect(() => {
    requestEventsArray.forEach(event => {
      document.dispatchEvent(new CustomEvent(event));
    });
  }, [requestEventsArray]);

  const experienceContext = useMemo(() => ({ experienceOptions }), [experienceOptions]);

  return (
    <ExperienceContext.Provider value={experienceContext}>{children}</ExperienceContext.Provider>
  );
};
